/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactElement } from 'react';

import type ImgixComponent from 'js/components/Imgix';

import { Grid, breakpoints } from '@coursera/cds-core';

import Container from 'bundles/degree-description/components/shared/Container';
import type { RichTextProps } from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';

const styles = {
  container: css`
    padding: var(--cds-spacing-600) var(--cds-spacing-200);
  `,

  description: (textColor?: string) => css`
    color: ${textColor || 'var(--cds-color-neutral-primary)'};
    padding-right: 16px;

    h3,
    p,
    a {
      color: ${textColor || 'var(--cds-color-neutral-primary)'};
    }

    a {
      /* Override for CDS links */
      color: ${textColor || 'var(--cds-color-interactive-primary)'};

      &:visited:not(.cds-link-focusVisible) {
        color: ${textColor || 'var(--cds-color-interactive-primary)'};
      }

      &:hover,
      &:focus {
        color: ${textColor || 'var(--cds-color-interactive-primary)'};
      }
    }

    ${breakpoints.down('sm')} {
      margin-top: var(--cds-spacing-300);
      padding-right: 0;
    }
  `,
  image: css`
    padding-left: 16px;
    width: 100%;

    img {
      border-radius: 8px;

      /* Need to set as important to override Imgix component styles */
      max-height: unset !important;
      max-width: unset !important;
      width: 100%;
    }

    ${breakpoints.down('sm')} {
      padding-left: 0;
    }
  `,
  hideOnMobile: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  hideOnDesktop: css`
    ${breakpoints.up('md')} {
      display: none;
    }
  `,
};

type Props = {
  description?: ReactElement<RichTextProps>;
  media?: ReactElement<ImgixComponent>;
  textColor?: string;
  trackingIndex?: number;
};

export const InfoSectionModule = ({ description, media, textColor, trackingIndex }: Props) => {
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('value_proposition', trackingIndex);

  return (
    <div ref={pageSectionRef}>
      <Container css={styles.container}>
        <Grid container justifyContent="space-between" alignItems="center">
          {media && (
            <Grid item sm={12} css={[styles.image, styles.hideOnDesktop]}>
              {media}
            </Grid>
          )}

          {description && (
            <Grid item sm={12} md={6} css={styles.description(textColor)}>
              {description}
            </Grid>
          )}

          {media && (
            <Grid item sm={12} md={6} css={[styles.image, styles.hideOnMobile]}>
              {media}
            </Grid>
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default InfoSectionModule;
